import type { SystemStyleObject } from "@styled-system/css";
import type { Theme } from "@emotion/react";
import { createComponent, createHook } from "reakit-system";
import { useTheme } from "@emotion/react";
import { useText } from "../Text/Text";
import { cx, ecss } from "../reexports/emotion";
import { useStyles } from "../hooks";
import type { LinkHTMLProps, LinkKind, LinkOptions, LinkProps } from "./types";
import { getIconDataImg } from "./getIcon";

export type { LinkProps };

const linkStyles = {
  borderRadius: 4,
  color: "linkColor",
  ":hover": {
    color: "linkHoverColor",
  },
  ":active": {
    color: "linkActiveColor",
  },
  ":visited": {
    color: "linkVisitedColor",
    ":hover": {
      color: "linkVisitedHoverColor",
    },
    ":active": {
      color: "linkVisitedActiveColor",
    },
  },
} as SystemStyleObject;

function linkIconStyles(kind: LinkKind, theme: Theme) {
  return {
    borderRadius: 4,
    paddingRight: "1.5em",
    backgroundImage: getIconDataImg(kind, "default", theme),
    backgroundSize: "1.25em 1.25em",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right center",
    color: "linkColor",

    ":hover": {
      color: "linkHoverColor",
      backgroundImage: getIconDataImg(kind, "hover", theme),
    },

    ":active": {
      color: "linkActiveColor",
      backgroundImage: getIconDataImg(kind, "active", theme),
    },
  };
}

export const useLink = createHook<LinkOptions, LinkHTMLProps>({
  name: "Link",
  compose: [useStyles, useText],
  keys: ["kind"],
  useOptions(options) {
    const theme = useTheme();

    const iconStyles = options.kind ? linkIconStyles(options.kind, theme) : {};

    return {
      ...options,
      styles: {
        ...options.styles,
        ...linkStyles,
        ...iconStyles,
        textDecoration: "underline",
        "&:focus, &:focus-visible": {
          outlineStyle: "solid",
          outlineWidth: "4px",
          outlineOffset: "0",
          outlineColor: "focus",
          borderRadius: "4px",
        },
      },
    };
  },
  useProps(options, props) {
    let { target } = props;

    if (!target && options.kind === "external") {
      target = "_blank";
    }

    return {
      ...props,
      className: cx(props.className, ecss({ label: "Link" })),
      target,
    };
  },
});

/**
 * @beta
 */
export const Link = createComponent<"a", LinkOptions>({
  as: "a",
  useHook: useLink,
  memo: true,
});
